import { AxiosInstance, AxiosResponse } from "axios"
import type {
  CreateProjectBody,
  UpdateProjectBody,
  PROJECT_FILTER,
  PROJECT_TYPE,
} from "types/project.types"
/**
 * Project API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Fetches the list of projects.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param filter - The filter to apply to the projects - owned, member, both, all.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function getProjects(
  axiosInstance: AxiosInstance,
  filter: PROJECT_FILTER
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/available-projects?filter=${filter}&metadata=true`
  )
  return response
}

/**
 * Fetches information for a specific project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function getProjectInfo(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/project-get?id=${id}&metadata=true`
  )
  return response
}

/**
 * Creates a new project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param body - The body of the request containing project details.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function createProject(
  axiosInstance: AxiosInstance,
  body: CreateProjectBody
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.post(
    `/project-create?type=${body.type}`,
    {
      name: body.name,
      goal: body.goal,
      type: body.type,
      status: "DESIGNING",
      languages: ["English"],
      designLanguage: "English",
    }
  )
  return response
}

/**
 * Deletes a project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function deleteProject(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.delete(`/project-delete?id=${id}`)
  return response
}

/**
 * Updates an existing project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param body - The body of the request containing updated project details.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function updateProject(
  axiosInstance: AxiosInstance,
  body: UpdateProjectBody
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.post(`/project-update?id=${body.id}`, {
    name: body.name,
    goal: body.goal,
    status: body.status,
    designLanguage: body.designLanguage,
    languages: body.languages,
    type: body.type,
  })
  return response
}

/**
 * Fetches the members of a specific project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function projectMembers(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get(`/list-project-members?id=${id}`)
  return response
}

/**
 * Adds a member to a specific project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param member_name - The name of the member to add.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function projectMemberAdd(
  axiosInstance: AxiosInstance,
  id: string,
  member_name: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/project-member-add?id=${id}&member=${encodeURIComponent(member_name)}`
  )
  return response
}

/**
 * Removes a member from a specific project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param member_name - The name of the member to remove.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function projectMemberRemove(
  axiosInstance: AxiosInstance,
  id: string,
  member_name: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.delete(
    `/project-member-remove?id=${id}&member=${encodeURIComponent(member_name)}`
  )
  return response
}

/**
 * Archives a specific project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function archiveProject(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/project-archive?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Unarchives a specific project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function unarchiveProject(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/project-unarchive?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Fetches the list of available archived projects.
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function availableArchivedProjects(
  axiosInstance: AxiosInstance
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/available-archived-projects?metadata=true`
  )
  return response
}

/**
 * Copies an existing project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param copy - The fields to copy (e.g., metadata, qnr, data, docs, queries).
 * @param name - The new name for the copied project, if empty will append -copy to original project name.
 * @param newOwner - The owner of the copied project.
 * @param history - What happens to the copied project's history: "clean" starts it empty,
 *   "full" copies the source's history as it is, and "wave" starts it with a "wave.forked" record
 *   and adds the copy to the source's tracker. A wave copy whose link failed is an error answer
 *   that still names the copy (`projectId`): the copy exists and can be added to the tracker.
 * @returns The response from the API (the new project's id).
 * @throws Will throw an error if the request fails, including a refused copy.
 */
export type CopyHistoryMode = "clean" | "full" | "wave"

export async function copyProject(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  copy: string,
  name: string,
  newOwner: string,
  history: CopyHistoryMode
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/project-copy?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}&copy=${copy}&name=${encodeURIComponent(name)}&owner=${encodeURIComponent(newOwner)}&history=${history}`
  )
  return response
}

/**
 * Moves a project to a new owner.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The current owner of the project.
 * @param newOwner - The new owner of the project.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function moveProject(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  newOwner: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(
    `/project-move?id=${id}&hint_owner=${encodeURIComponent(owner)}&owner=${encodeURIComponent(newOwner)}`
  )
  return response
}

/**
 * Fetches the type of project from ID.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project type ID to fetch.
 * @returns The response from the API containing project type.
 * @throws Will throw an error if the request fails.
 */
export async function getProjectType(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<PROJECT_TYPE>> {
  const response = await axiosInstance.get(`/project-type?id=${id}`)
  return response
}

/**
 * Fetches the caller's named project folders (used for grouping projects in the UI).
 * Returns a JSON array of folder paths (e.g. ["Trackers", "Clients/Acme"]).
 */
export async function getProjectFolders(
  axiosInstance: AxiosInstance
): Promise<AxiosResponse<string[]>> {
  return axiosInstance.get(`/project-folders`)
}

/**
 * Creates a new (empty) project folder for the caller. Idempotent.
 */
export async function createProjectFolder(
  axiosInstance: AxiosInstance,
  folder: string
): Promise<AxiosResponse<unknown>> {
  return axiosInstance.post(
    `/project-folder-create?folder=${encodeURIComponent(folder)}`
  )
}

/**
 * Deletes a folder. Any descendants are also dropped. Projects assigned to a removed
 * folder become ungrouped (their `Folder` metadata key is cleared).
 */
export async function deleteProjectFolder(
  axiosInstance: AxiosInstance,
  folder: string
): Promise<AxiosResponse<unknown>> {
  return axiosInstance.delete(
    `/project-folder-delete?folder=${encodeURIComponent(folder)}`
  )
}

/**
 * Renames a folder. Descendants and any owned projects assigned to the path are rewritten.
 */
export async function renameProjectFolder(
  axiosInstance: AxiosInstance,
  oldPath: string,
  newPath: string
): Promise<AxiosResponse<unknown>> {
  return axiosInstance.post(
    `/project-folder-rename?folder=${encodeURIComponent(oldPath)}&new=${encodeURIComponent(newPath)}`
  )
}

/**
 * Sets a project's folder assignment by patching its metadata. Pass an empty string to
 * make the project ungrouped (the backend removes the key when the value is empty).
 */
export async function setProjectFolder(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  folder: string
): Promise<AxiosResponse<unknown>> {
  return axiosInstance.post(
    `/project-update?id=${id}&hint_owner=${encodeURIComponent(owner)}`,
    { Folder: folder }
  )
}
